import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Upload, MapPin, Tag, Loader2, CheckCircle, Camera, X, Hash, Truck, Ruler, Weight, Box, FileText, Printer, QrCode } from 'lucide-react'
import { useCreateItem } from '../hooks/useItems'
import { useShell } from '../components/PageShell'
import { useMobile } from '../contexts/MobileContext'
import PrintButton from '../components/print/PrintButton'
import PrintDialog from '../components/print/PrintDialog'
import { QRScannerModal } from '../components/admin/QRScannerModal'

interface IntakeSection {
  id: string
  name: string
  description: string
  icon: React.ComponentType<any>
  glowColor: string
  order: number
}

const intakeSections: IntakeSection[] = [
  {
    id: 'basic',
    name: 'Basic Information',
    description: 'Core item identification data',
    icon: Package,
    glowColor: '#FF6B35',
    order: 1
  },
  {
    id: 'images',
    name: 'Images',
    description: 'Visual documentation matrix',
    icon: Camera,
    glowColor: '#00F0FF',
    order: 2
  },
  {
    id: 'source',
    name: 'Source & Acquisition',
    description: 'Item origin tracking',
    icon: FileText,
    glowColor: '#B91BFF',
    order: 3
  },
  {
    id: 'location',
    name: 'Location & Storage',
    description: 'Warehouse positioning system',
    icon: MapPin,
    glowColor: '#FFD93D',
    order: 4
  },
  {
    id: 'shipping',
    name: 'Shipping Details',
    description: 'Dimensional weight matrix',
    icon: Truck,
    glowColor: '#6BCF7F',
    order: 5
  }
]

const InputField = ({ label, value, onChange, type = 'text', placeholder = '', required = false, onQRScan = null, step = undefined, error = '' }: any) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{
      display: 'block',
      fontSize: '11px',
      color: error ? '#FF0000' : '#8A95AA',
      letterSpacing: '1px',
      marginBottom: '8px',
      textTransform: 'uppercase',
      fontWeight: 600
    }}>
      {label} {required && <span style={{ color: '#FF6B35' }}>*</span>}
    </label>
    <div style={{ position: 'relative', display: 'flex', gap: '8px', alignItems: 'center' }}>
      <input
        type={type}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          flex: 1,
          height: '44px',
          padding: '12px 16px',
          background: 'rgba(26, 31, 46, 0.6)',
          border: `2px solid ${error ? '#FF0000' : '#FF6B35'}`,
          borderRadius: '6px',
          color: '#E0E6F0',
          fontSize: '14px',
          fontFamily: 'Rajdhani, sans-serif',
          outline: 'none',
          transition: 'all 0.2s',
          boxShadow: `0 0 10px ${error ? 'rgba(255, 0, 0, 0.3)' : 'rgba(255, 107, 53, 0.2)'}`
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = error ? '#FF0000' : '#FF6B35'
          e.currentTarget.style.boxShadow = error ? '0 0 20px rgba(255, 0, 0, 0.5)' : '0 0 20px rgba(255, 107, 53, 0.4)'
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? '#FF0000' : '#FF6B35'
          e.currentTarget.style.boxShadow = error ? '0 0 10px rgba(255, 0, 0, 0.3)' : '0 0 10px rgba(255, 107, 53, 0.2)'
        }}
      />
      {onQRScan && (
        <button
          type="button"
          onClick={onQRScan}
          style={{
            height: '44px',
            width: '44px',
            padding: '10px',
            background: 'rgba(255, 107, 53, 0.2)',
            border: '2px solid #FF6B35',
            borderRadius: '6px',
            color: '#FF6B35',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            boxShadow: '0 0 10px rgba(255, 107, 53, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 53, 0.3)'
            e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 53, 0.2)'
            e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.2)'
          }}
        >
          <QrCode style={{ width: 20, height: 20 }} />
        </button>
      )}
    </div>
    {error && (
      <div style={{
        marginTop: '4px',
        fontSize: '12px',
        color: '#FF0000',
        fontFamily: 'Rajdhani, sans-serif',
        fontWeight: 600
      }}>
        {error}
      </div>
    )}
  </div>
)

const SelectField = ({ label, value, onChange, options, required = false, onQRScan = null }: any) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{
      display: 'block',
      fontSize: '11px',
      color: '#8A95AA',
      letterSpacing: '1px',
      marginBottom: '8px',
      textTransform: 'uppercase',
      fontWeight: 600
    }}>
      {label} {required && <span style={{ color: '#FF6B35' }}>*</span>}
    </label>
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <select
        value={value}
        onChange={onChange}
        style={{
          flex: 1,
          height: '44px',
          padding: '12px 16px',
          background: 'rgba(26, 31, 46, 0.6)',
          border: '2px solid #FF6B35',
          borderRadius: '6px',
          color: '#E0E6F0',
          fontSize: '14px',
          fontFamily: 'Rajdhani, sans-serif',
          outline: 'none',
          transition: 'all 0.2s',
          boxShadow: '0 0 10px rgba(255, 107, 53, 0.2)',
          cursor: 'pointer'
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#FF6B35'
          e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4)'
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#FF6B35'
          e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.2)'
        }}
      >
        {options.map((opt: any) => (
          <option key={opt.value} value={opt.value} style={{ background: '#1A1F2E', color: '#E0E6F0' }}>
            {opt.label}
          </option>
        ))}
      </select>
      {onQRScan && (
        <button
          type="button"
          onClick={onQRScan}
          style={{
            height: '44px',
            width: '44px',
            padding: '10px',
            background: 'rgba(255, 107, 53, 0.2)',
            border: '2px solid #FF6B35',
            borderRadius: '6px',
            color: '#FF6B35',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            boxShadow: '0 0 10px rgba(255, 107, 53, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 53, 0.3)'
            e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 53, 0.2)'
            e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.2)'
          }}
        >
          <QrCode style={{ width: 20, height: 20 }} />
        </button>
      )}
    </div>
  </div>
)

export default function IntakePage() {
  const { toast } = useShell()
  const { isMobile } = useMobile()
  const navigate = useNavigate()
  const createItemMutation = useCreateItem()

  // API Data State
  const [categories, setCategories] = useState<any[]>([])
  const [locations, setLocations] = useState<any[]>([])
  const [totes, setTotes] = useState<any[]>([])
  const [shelves, setShelves] = useState<any[]>([])
  const [customFields, setCustomFields] = useState<any[]>([])
  const [packageTypes, setPackageTypes] = useState<any[]>([])

  const [formData, setFormData] = useState<{
    name: string
    sku: string
    brand: string
    model: string
    description: string
    notes: string
    condition: 'New' | 'Used - Like New' | 'Used - Good' | 'Used - Acceptable' | 'For Parts'
    status: 'active' | 'inactive'
    quantity: number
    reserved_quantity: number
    cost: number | string
    target_price: number | string
    category_id: string
    location_id: string
    shelf_id: string
    tote_id: string
    source: 'purchase' | 'donation' | 'consignment' | 'return'
    acquisition_date: string
    weight_lbs: number
    weight_oz: number
    length_cm: number
    width_cm: number
    height_cm: number
    shipping_instructions: string
    package_type: string
  }>({
    name: '',
    sku: '',
    brand: '',
    model: '',
    description: '',
    notes: '',
    condition: 'New',
    status: 'active',
    quantity: 1,
    reserved_quantity: 0,
    cost: 0,
    target_price: 0,
    category_id: '',
    location_id: '',
    shelf_id: '',
    tote_id: '',
    source: 'purchase',
    acquisition_date: '',
    weight_lbs: 0,
    weight_oz: 0,
    length_cm: 0,
    width_cm: 0,
    height_cm: 0,
    shipping_instructions: '',
    package_type: 'box',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedImages, setSelectedImages] = useState<File[]>([])
  const [primaryImageIndex, setPrimaryImageIndex] = useState<number | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [showPrintDialog, setShowPrintDialog] = useState(false)
  const [lastCreatedItem, setLastCreatedItem] = useState<any>(null)
  const [showQRScanner, setShowQRScanner] = useState(false)
  const [qrScanField, setQrScanField] = useState<string | null>(null)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  // Fetch API data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, locationsRes, totesRes, shelvesRes, systemPicksRes] = await Promise.all([
          fetch('/api/categories-pg'),
          fetch('/api/locations-pg?is_processing=true'),
          fetch('/api/totes-pg'),
          fetch('/api/shelves-pg'),
          fetch('/api/system-picks-pg')
        ])

        const categoriesData = await categoriesRes.json()
        const locationsData = await locationsRes.json()
        const totesData = await totesRes.json()
        const shelvesData = await shelvesRes.json()
        const systemPicksData = await systemPicksRes.json()

        if (categoriesData.success) setCategories(categoriesData.data || [])
        if (locationsData.success) setLocations(locationsData.data || [])
        if (totesData.success) setTotes(totesData.data || [])
        if (shelvesData.success) setShelves(shelvesData.data || [])
        if (systemPicksData.success) {
          const packageTypeOptions = systemPicksData.data.filter((pick: any) => pick.category === 'package-type')
          setPackageTypes(packageTypeOptions)
        }
      } catch (error) {
        console.error('Failed to fetch data:', error)
      }
    }

    fetchData()
  }, [])

  // Fetch custom fields when category changes
  useEffect(() => {
    if (formData.category_id) {
      const fetchCustomFields = async () => {
        try {
          const response = await fetch(`/api/category-fields-pg?category_id=${formData.category_id}`)
          const data = await response.json()
          if (data.success) {
            setCustomFields(data.data || [])
          }
        } catch (error) {
          console.error('Failed to fetch custom fields:', error)
        }
      }
      fetchCustomFields()
    } else {
      setCustomFields([])
    }
  }, [formData.category_id])

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleQRScan = (field: string) => {
    setQrScanField(field)
    setShowQRScanner(true)
  }

  const handleQRResult = (result: string) => {
    if (qrScanField === 'sku') {
      setFormData(prev => ({ ...prev, sku: result }))
    } else if (qrScanField === 'location') {
      // Parse LOCATION:id format and find location
      const locationId = result.split(':')[1]
      setFormData(prev => ({ ...prev, location_id: locationId }))
    } else if (qrScanField === 'tote') {
      const toteId = result.split(':')[1]
      setFormData(prev => ({ ...prev, tote_id: toteId }))
    } else if (qrScanField === 'shelf') {
      const shelfId = result.split(':')[1]
      setFormData(prev => ({ ...prev, shelf_id: shelfId }))
    }
    setShowQRScanner(false)
    setQrScanField(null)
  }

  // Filter shelves and totes based on selected location
  const filteredShelves = formData.location_id
    ? shelves.filter(shelf => shelf.location_id === formData.location_id)
    : shelves

  const filteredTotes = formData.location_id
    ? totes.filter(tote => tote.location_id === formData.location_id)
    : totes

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setSelectedImages(prev => [...prev, ...newFiles])
      if (selectedImages.length === 0 && newFiles.length > 0) {
        setPrimaryImageIndex(0)
      }
    }
  }

  const handleRemoveImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index))
    if (primaryImageIndex === index) {
      setPrimaryImageIndex(selectedImages.length > 1 ? 0 : null)
    } else if (primaryImageIndex !== null && primaryImageIndex > index) {
      setPrimaryImageIndex(primaryImageIndex - 1)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '', sku: '', brand: '', model: '', description: '', notes: '',
      condition: 'New', status: 'active', quantity: 1, reserved_quantity: 0,
      cost: 0, target_price: 0, category_id: '', location_id: '', shelf_id: '',
      tote_id: '', source: 'purchase', acquisition_date: '', weight_lbs: 0,
      weight_oz: 0, length_cm: 0, width_cm: 0, height_cm: 0,
      shipping_instructions: '', package_type: 'box',
    })
    setSelectedImages([])
    setPrimaryImageIndex(null)
    setErrors({})
  }

  const handleSubmit = async (e: React.FormEvent, saveAsDraft = false) => {
    e.preventDefault()

    // Validate required fields
    const validationErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      validationErrors.name = 'Title is required'
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      toast({
        title: 'Validation Error',
        description: Object.values(validationErrors).join(', '),
        type: 'error'
      })
      return
    }

    setErrors({})
    setIsSubmitting(true)

    try {
      const data = new FormData()
      data.append('title', formData.name)
      if (formData.sku) data.append('sku', formData.sku)
      if (formData.brand) data.append('brand', formData.brand)
      if (formData.model) data.append('model', formData.model)
      if (formData.description) data.append('description', formData.description)
      if (formData.notes) data.append('notes', formData.notes)
      data.append('condition', formData.condition)
      data.append('status', saveAsDraft ? 'draft' : formData.status)
      data.append('quantity', formData.quantity.toString())
      data.append('reserved_quantity', formData.reserved_quantity.toString())
      if (formData.cost) data.append('cost', formData.cost.toString())
      if (formData.target_price) data.append('target_price', formData.target_price.toString())
      if (formData.category_id) data.append('category_id', formData.category_id)
      if (formData.location_id) data.append('location_id', formData.location_id)
      if (formData.shelf_id) data.append('shelf_id', formData.shelf_id)
      if (formData.tote_id) data.append('tote_id', formData.tote_id)

      if (formData.weight_lbs) data.append('weight_lbs', formData.weight_lbs.toString())
      if (formData.weight_oz) data.append('weight_oz', formData.weight_oz.toString())
      if (formData.length_cm) data.append('length', (formData.length_cm / 2.54).toString())
      if (formData.width_cm) data.append('width', (formData.width_cm / 2.54).toString())
      if (formData.height_cm) data.append('height', (formData.height_cm / 2.54).toString())
      if (formData.shipping_instructions) data.append('special_instructions', formData.shipping_instructions)
      if (formData.package_type) data.append('package_type', formData.package_type)

      selectedImages.forEach((file, index) => {
        data.append(`image_${index}`, file)
      })

      if (primaryImageIndex !== null) {
        data.append('primaryImageIndex', primaryImageIndex.toString())
      }

      const response = await fetch('/api/items', {
        method: 'POST',
        body: data
      })

      const result = await response.json()

      if (result.success) {
        // Store created item for potential printing and actions
        setLastCreatedItem(result.data)

        // Trigger AI analysis asynchronously (fire and forget - don't block UI)
        if (!saveAsDraft && result.data.id) {
          fetch('/api/ai/analyze-item', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ item_id: result.data.id })
          }).then(aiResponse => {
            if (aiResponse.ok) {
              console.log('AI analysis queued for item', result.data.ic)
            }
          }).catch(err => {
            console.error('Failed to queue AI analysis:', err)
          })
        }

        // Show success dialog with actions
        setShowSuccessDialog(true)
      } else {
        toast({ title: 'Error', description: result.error || 'Failed to create item', type: 'error' })
      }
    } catch (error: any) {
      toast({ title: 'Error', description: 'Failed to create item', type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <h1 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '48px',
          fontWeight: 700,
          color: '#FF6B35',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          margin: 0,
          textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
        }}>
          INVENTORY INTAKE
        </h1>
        <p style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '16px',
          color: '#8A95AA',
          margin: '8px 0 0 0',
          letterSpacing: '1px'
        }}>
          RAPID ITEM REGISTRATION INTERFACE
        </p>
      </div>

      {/* Form Sections Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {/* Basic Information */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #FF6B35',
          borderRadius: '8px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(255, 107, 53, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              background: 'rgba(255, 107, 53, 0.2)',
              border: '2px solid #FF6B35',
              borderRadius: '8px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Package style={{ width: 24, height: 24, color: '#FF6B35' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0,
              textShadow: '0 0 10px rgba(255, 107, 53, 0.6)'
            }}>
              BASIC INFORMATION
            </h3>
          </div>

          <InputField
            label="Item Name"
            value={formData.name}
            onChange={(e: any) => handleInputChange('name', e.target.value)}
            placeholder="Enter item name"
            required
            error={errors.name}
          />
          <InputField
            label="SKU"
            value={formData.sku}
            onChange={(e: any) => handleInputChange('sku', e.target.value)}
            placeholder="Enter SKU"
            onQRScan={() => handleQRScan('sku')}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <InputField
              label="Brand"
              value={formData.brand}
              onChange={(e: any) => handleInputChange('brand', e.target.value)}
              placeholder="Brand"
            />
            <InputField
              label="Model"
              value={formData.model}
              onChange={(e: any) => handleInputChange('model', e.target.value)}
              placeholder="Model"
            />
          </div>
          <InputField
            label="Description"
            value={formData.description}
            onChange={(e: any) => handleInputChange('description', e.target.value)}
            placeholder="Item description"
          />
          <SelectField
            label="Category"
            value={formData.category_id}
            onChange={(e: any) => handleInputChange('category_id', e.target.value)}
            options={[
              { value: '', label: 'Select category...' },
              ...categories.map(cat => ({ value: cat.id, label: cat.name }))
            ]}
          />
          <SelectField
            label="Condition"
            value={formData.condition}
            onChange={(e: any) => handleInputChange('condition', e.target.value)}
            options={[
              { value: 'New', label: 'New' },
              { value: 'Like New', label: 'Like New' },
              { value: 'Excellent', label: 'Excellent' },
              { value: 'Good', label: 'Good' },
              { value: 'Fair', label: 'Fair' },
              { value: 'Poor', label: 'Poor' }
            ]}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <InputField
              label="Quantity"
              type="text"
              value={formData.quantity}
              onChange={(e: any) => {
                const val = e.target.value
                if (val === '' || /^\d+$/.test(val)) {
                  handleInputChange('quantity', val === '' ? 0 : parseInt(val))
                }
              }}
              placeholder="0"
            />
            <InputField
              label="Reserved Qty"
              type="text"
              value={formData.reserved_quantity}
              onChange={(e: any) => {
                const val = e.target.value
                if (val === '' || /^\d+$/.test(val)) {
                  handleInputChange('reserved_quantity', val === '' ? 0 : parseInt(val))
                }
              }}
              placeholder="0"
            />
          </div>
          <div style={{
            padding: '12px 16px',
            background: 'rgba(255, 107, 53, 0.1)',
            border: '2px solid #FF6B35',
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <span style={{ color: '#8A95AA', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 600 }}>
              Available Quantity:
            </span>
            <span style={{ color: '#FF6B35', fontSize: '16px', fontWeight: 700, marginLeft: '8px' }}>
              {formData.quantity - formData.reserved_quantity}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <InputField
              label="Cost"
              type="text"
              value={formData.cost === 0 ? '' : formData.cost.toString()}
              onChange={(e: any) => {
                const val = e.target.value
                // Allow empty string, numbers, and decimals (up to 2 decimal places)
                if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                  handleInputChange('cost', val === '' ? 0 : val)
                }
              }}
              placeholder="0.00"
            />
            <InputField
              label="Target Price"
              type="text"
              value={formData.target_price === 0 ? '' : formData.target_price.toString()}
              onChange={(e: any) => {
                const val = e.target.value
                // Allow empty string, numbers, and decimals (up to 2 decimal places)
                if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                  handleInputChange('target_price', val === '' ? 0 : val)
                }
              }}
              placeholder="0.00"
            />
          </div>

          {/* Custom Fields (rendered when category selected) */}
          {customFields.length > 0 && (
            <>
              <div style={{
                borderTop: '1px solid rgba(255, 107, 53, 0.3)',
                marginTop: '20px',
                marginBottom: '20px'
              }} />
              <div style={{
                marginBottom: '12px',
                color: '#FFD93D',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                ⚡ Category-Specific Fields
              </div>
              {customFields.map((field: any) => {
                const fieldKey = `custom_${field.id}` as keyof typeof formData
                const fieldValue = formData[fieldKey] || ''

                // Render based on field type
                if (field.field_type === 'select') {
                  return (
                    <div key={field.id} style={{ marginBottom: '16px' }}>
                      <label style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '11px',
                        color: '#8A95AA',
                        marginBottom: '6px',
                        display: 'block',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        {field.name} {field.required && '*'}
                      </label>
                      <select
                        value={fieldValue}
                        onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                        required={field.required}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '16px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select {field.name}</option>
                        {field.options && field.options.map((option: string) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  )
                } else if (field.field_type === 'boolean') {
                  return (
                    <div key={field.id} style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px',
                        color: '#E0E6F0',
                        fontWeight: 600
                      }}>
                        <input
                          type="checkbox"
                          checked={fieldValue === 'true' || String(fieldValue) === 'true'}
                          onChange={(e) => handleInputChange(fieldKey, e.target.checked.toString())}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        {field.name}
                      </label>
                    </div>
                  )
                } else {
                  // Text, number, date
                  return (
                    <InputField
                      key={field.id}
                      label={field.name}
                      value={fieldValue}
                      onChange={(e: any) => handleInputChange(fieldKey, e.target.value)}
                      type={field.field_type}
                      placeholder={field.placeholder || `Enter ${field.name}`}
                      required={field.required}
                    />
                  )
                }
              })}
            </>
          )}
        </div>

        {/* Images */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #00F0FF',
          borderRadius: '8px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(0, 240, 255, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              background: 'rgba(0, 240, 255, 0.2)',
              border: '2px solid #00F0FF',
              borderRadius: '8px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Camera style={{ width: 24, height: 24, color: '#00F0FF' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#00F0FF',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0,
              textShadow: '0 0 10px rgba(0, 240, 255, 0.6)'
            }}>
              IMAGES
            </h3>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #00F0FF',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'rgba(0, 240, 255, 0.05)',
              transition: 'all 0.2s',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
              e.currentTarget.style.borderColor = '#00F0FF'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.05)'
              e.currentTarget.style.borderColor = '#00F0FF'
            }}
          >
            {selectedImages.length === 0 ? (
              <>
                <Upload style={{ width: 48, height: 48, color: '#00F0FF', marginBottom: '16px' }} />
                <p style={{ color: '#E0E6F0', fontSize: '14px', margin: 0, letterSpacing: '1px' }}>
                  CLICK TO UPLOAD IMAGES
                </p>
                <p style={{ color: '#8A95AA', fontSize: '12px', margin: '8px 0 0 0' }}>
                  Up to 20 images (PNG, JPG, GIF)
                </p>
              </>
            ) : (
              <div onClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  {selectedImages.map((file, index) => (
                    <div key={index} style={{ position: 'relative', paddingBottom: '100%' }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: primaryImageIndex === index ? '2px solid #00F0FF' : '1px solid #8A95AA'
                        }}
                      />
                      <button
                        onClick={() => handleRemoveImage(index)}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          background: '#FF0000',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        <X style={{ width: 12, height: 12, color: '#FFFFFF' }} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(0, 240, 255, 0.2)',
                    border: '2px solid #00F0FF',
                    borderRadius: '6px',
                    color: '#00F0FF',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: 'pointer',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}
                >
                  ADD MORE IMAGES
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Source & Acquisition */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #B91BFF',
          borderRadius: '8px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0 20px rgba(185, 27, 255, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(185, 27, 255, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              background: 'rgba(185, 27, 255, 0.2)',
              border: '2px solid #B91BFF',
              borderRadius: '8px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileText style={{ width: 24, height: 24, color: '#B91BFF' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#B91BFF',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0,
              textShadow: '0 0 10px rgba(185, 27, 255, 0.6)'
            }}>
              SOURCE & ACQUISITION
            </h3>
          </div>

          <SelectField
            label="Source"
            value={formData.source}
            onChange={(e: any) => handleInputChange('source', e.target.value)}
            options={[
              { value: 'purchase', label: 'Purchase' },
              { value: 'donation', label: 'Donation' },
              { value: 'trade', label: 'Trade' },
              { value: 'found', label: 'Found' },
              { value: 'return', label: 'Return' }
            ]}
          />
          <InputField
            label="Acquisition Date"
            type="date"
            value={formData.acquisition_date}
            onChange={(e: any) => handleInputChange('acquisition_date', e.target.value)}
          />
          <InputField
            label="Notes"
            value={formData.notes}
            onChange={(e: any) => handleInputChange('notes', e.target.value)}
            placeholder="Additional notes about the item"
          />
        </div>

        {/* Location & Storage */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #FFD93D',
          borderRadius: '8px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0 20px rgba(255, 217, 61, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(255, 217, 61, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              background: 'rgba(255, 217, 61, 0.2)',
              border: '2px solid #FFD93D',
              borderRadius: '8px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MapPin style={{ width: 24, height: 24, color: '#FFD93D' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#FFD93D',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0,
              textShadow: '0 0 10px rgba(255, 217, 61, 0.6)'
            }}>
              LOCATION & STORAGE
            </h3>
          </div>

          <SelectField
            label="Location"
            value={formData.location_id}
            onChange={(e: any) => handleInputChange('location_id', e.target.value)}
            options={[
              { value: '', label: 'Select location...' },
              ...locations.map(loc => ({ value: loc.id, label: loc.name }))
            ]}
            onQRScan={() => handleQRScan('location')}
          />
          <SelectField
            label="Shelf"
            value={formData.shelf_id}
            onChange={(e: any) => handleInputChange('shelf_id', e.target.value)}
            options={[
              { value: '', label: 'Select shelf...' },
              ...filteredShelves.map(shelf => ({ value: shelf.id, label: shelf.name }))
            ]}
            onQRScan={() => handleQRScan('shelf')}
          />
          <SelectField
            label="Tote"
            value={formData.tote_id}
            onChange={(e: any) => handleInputChange('tote_id', e.target.value)}
            options={[
              { value: '', label: 'Select tote...' },
              ...filteredTotes.map(tote => ({ value: tote.id, label: tote.name || tote.code || tote.id }))
            ]}
            onQRScan={() => handleQRScan('tote')}
          />
        </div>

        {/* Shipping Details */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #6BCF7F',
          borderRadius: '8px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0 20px rgba(107, 207, 127, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(107, 207, 127, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              background: 'rgba(107, 207, 127, 0.2)',
              border: '2px solid #6BCF7F',
              borderRadius: '8px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Truck style={{ width: 24, height: 24, color: '#6BCF7F' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#6BCF7F',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0,
              textShadow: '0 0 10px rgba(107, 207, 127, 0.6)'
            }}>
              SHIPPING DETAILS
            </h3>
          </div>

          {/* Weight Row */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '12px' }}>
            <div style={{ width: '120px' }}>
              <InputField
                label="Weight (lbs)"
                type="text"
                value={formData.weight_lbs}
                onChange={(e: any) => {
                  const val = e.target.value
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    handleInputChange('weight_lbs', val === '' ? 0 : parseFloat(val))
                  }
                }}
                placeholder="0.0"
              />
            </div>
            <div style={{ width: '120px' }}>
              <InputField
                label="Weight (oz)"
                type="text"
                value={formData.weight_oz}
                onChange={(e: any) => {
                  const val = e.target.value
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    handleInputChange('weight_oz', val === '' ? 0 : parseFloat(val))
                  }
                }}
                placeholder="0.0"
              />
            </div>
          </div>

          {/* Dimensions Row */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '12px' }}>
            <div style={{ width: '120px' }}>
              <InputField
                label="Length (cm)"
                type="text"
                value={formData.length_cm}
                onChange={(e: any) => {
                  const val = e.target.value
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    handleInputChange('length_cm', val === '' ? 0 : parseFloat(val))
                  }
                }}
                placeholder="0.0"
              />
            </div>
            <div style={{ width: '120px' }}>
              <InputField
                label="Width (cm)"
                type="text"
                value={formData.width_cm}
                onChange={(e: any) => {
                  const val = e.target.value
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    handleInputChange('width_cm', val === '' ? 0 : parseFloat(val))
                  }
                }}
                placeholder="0.0"
              />
            </div>
            <div style={{ width: '120px' }}>
              <InputField
                label="Height (cm)"
                type="text"
                value={formData.height_cm}
                onChange={(e: any) => {
                  const val = e.target.value
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    handleInputChange('height_cm', val === '' ? 0 : parseFloat(val))
                  }
                }}
                placeholder="0.0"
              />
            </div>
          </div>

          {/* Package Type Row */}
          <div style={{ width: '200px' }}>
            <SelectField
              label="Package Type"
              value={formData.package_type}
              onChange={(e: any) => handleInputChange('package_type', e.target.value)}
              options={packageTypes.map((pkg: any) => ({
                value: pkg.value,
                label: pkg.name
              }))}
            />
          </div>
        </div>

      </div>

      {/* Bottom Action Bar */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(255, 107, 53, 0.05))',
        border: '2px solid #FF6B35',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        bottom: '20px',
        boxShadow: '0 0 30px rgba(255, 107, 53, 0.3)',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{
          color: '#E0E6F0',
          fontSize: '14px',
          letterSpacing: '1px',
          fontWeight: 600
        }}>
          FORM READY FOR SUBMISSION
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            style={{
              background: 'rgba(138, 149, 170, 0.2)',
              border: '2px solid #8A95AA',
              borderRadius: '6px',
              padding: '12px 24px',
              color: '#E0E6F0',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Rajdhani, sans-serif',
              transition: 'all 0.2s'
            }}
            onClick={() => navigate(-1)}
          >
            <X style={{ width: 16, height: 16 }} />
            CANCEL
          </button>
          <button
            onClick={(e) => handleSubmit(e, true)}
            disabled={isSubmitting}
            style={{
              background: 'rgba(185, 27, 255, 0.2)',
              border: '2px solid #B91BFF',
              borderRadius: '6px',
              padding: '12px 24px',
              color: '#B91BFF',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: '0 0 10px rgba(185, 27, 255, 0.2)',
              fontFamily: 'Rajdhani, sans-serif',
              opacity: isSubmitting ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(185, 27, 255, 0.4)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.boxShadow = '0 0 10px rgba(185, 27, 255, 0.2)'
              }
            }}
          >
            <FileText style={{ width: 16, height: 16 }} />
            SAVE AS DRAFT
          </button>
          <button
            onClick={(e) => handleSubmit(e, false)}
            disabled={isSubmitting}
            style={{
              background: 'linear-gradient(135deg, #FF6B35, #F97316)',
              border: '2px solid #FF6B35',
              borderRadius: '6px',
              padding: '12px 32px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)',
              fontFamily: 'Rajdhani, sans-serif',
              opacity: isSubmitting ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 107, 53, 0.6)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4)'
              }
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                PROCESSING...
              </>
            ) : (
              <>
                <CheckCircle style={{ width: 16, height: 16 }} />
                SAVE
              </>
            )}
          </button>
          {lastCreatedItem && (
            <button
              onClick={() => setShowPrintDialog(true)}
              style={{
                background: 'rgba(0, 240, 255, 0.2)',
                border: '2px solid #00F0FF',
                borderRadius: '6px',
                padding: '12px 24px',
                color: '#00F0FF',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                boxShadow: '0 0 10px rgba(0, 240, 255, 0.2)',
                fontFamily: 'Rajdhani, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.4)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 240, 255, 0.2)'
              }}
            >
              <Printer style={{ width: 16, height: 16 }} />
              PRINT LABEL
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {lastCreatedItem && (
        <PrintDialog
          isOpen={showPrintDialog}
          onClose={() => {
            setShowPrintDialog(false)
          }}
          entityType="item"
          entityData={lastCreatedItem}
          categoryId={lastCreatedItem?.category_id}
        />
      )}

      <QRScannerModal
        isOpen={showQRScanner}
        onClose={() => {
          setShowQRScanner(false)
          setQrScanField(null)
        }}
        onScan={handleQRResult}
        title={`Scan QR Code for ${qrScanField?.toUpperCase()}`}
      />

      {/* Success Dialog */}
      {showSuccessDialog && lastCreatedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1A1F2E, #242938)',
            border: '2px solid #FF6B35',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 0 40px rgba(255, 107, 53, 0.4)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <CheckCircle style={{ width: 64, height: 64, color: '#6BCF7F', margin: '0 auto 16px' }} />
              <h2 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '32px',
                fontWeight: 700,
                color: '#FF6B35',
                textTransform: 'uppercase',
                margin: '0 0 8px 0',
                letterSpacing: '2px'
              }}>
                SUCCESS!
              </h2>
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '18px',
                color: '#E0E6F0',
                margin: 0
              }}>
                Item IC: <span style={{ color: '#FF6B35', fontWeight: 700 }}>{lastCreatedItem.ic?.replace(/^IC/, '')}</span> created successfully
              </p>
            </div>

            <div style={{
              display: 'grid',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => {
                  navigate(`/item/${lastCreatedItem.id}`)
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'rgba(255, 107, 53, 0.2)',
                  border: '2px solid #FF6B35',
                  borderRadius: '8px',
                  color: '#FF6B35',
                  fontSize: '16px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 107, 53, 0.3)'
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 107, 53, 0.2)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                📦 View Item Details
              </button>

              <button
                onClick={() => {
                  // Don't close success dialog - just open print dialog
                  setShowPrintDialog(true)
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'rgba(0, 240, 255, 0.2)',
                  border: '2px solid #00F0FF',
                  borderRadius: '8px',
                  color: '#00F0FF',
                  fontSize: '16px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 240, 255, 0.3)'
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                🖨️ Print Label
              </button>

              <button
                onClick={() => {
                  navigate('/search')
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'rgba(185, 27, 255, 0.2)',
                  border: '2px solid #B91BFF',
                  borderRadius: '8px',
                  color: '#B91BFF',
                  fontSize: '16px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(185, 27, 255, 0.3)'
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(185, 27, 255, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(185, 27, 255, 0.2)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                🔍 Go to Search
              </button>

              <button
                onClick={() => {
                  setShowSuccessDialog(false)
                  resetForm()
                  setLastCreatedItem(null)
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'rgba(107, 207, 127, 0.2)',
                  border: '2px solid #6BCF7F',
                  borderRadius: '8px',
                  color: '#6BCF7F',
                  fontSize: '16px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(107, 207, 127, 0.3)'
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(107, 207, 127, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                ➕ Add Another Item
              </button>
            </div>

            <button
              onClick={() => {
                setShowSuccessDialog(false)
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'transparent',
                border: '2px solid #8A95AA',
                borderRadius: '8px',
                color: '#8A95AA',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#E0E6F0'
                e.currentTarget.style.color = '#E0E6F0'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#8A95AA'
                e.currentTarget.style.color = '#8A95AA'
              }}
            >
              Stay on This Page
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
